import { DatePicker, Input, message, Modal, Select } from 'antd';
import axiosInstance from '../../../../../axios/axiosInstance';
import React, { useEffect, useState } from 'react';
import { ModePage } from 'common/enum';
import { ActiveCodeRequest } from 'model/request';
import processError from '../../../../../utils/processError';
import { Book } from '../../../../../model/response';
import { findAllBook } from '../../../../../service/bookService';
import { activeCodeStatusList } from '../../../../../utils/enumMapper';
import dayjs from 'dayjs';

interface Props {
  isOpen: boolean;
  onCloseModal: () => void;
  mode: ModePage;
  onBeforeSuccess: () => void;
  activeCodeId: string;
}

export default function ActiveCodeFormModal({ isOpen, onCloseModal, mode, onBeforeSuccess, activeCodeId }: Props) {
  const [newActiveCode, setNewActiveCode] = useState<ActiveCodeRequest | null>(null);
  const [books, setBooks] = useState<Book[]>([]);

  useEffect(() => {
    findAllBook().then(setBooks).catch(processError);
  }, []);

  const onInputField = (e: any, fieldName: string) => {
    const newActiveCodeTemp = { ...newActiveCode } as any;
    newActiveCodeTemp[fieldName] = e.target.value;

    setNewActiveCode(newActiveCodeTemp);
  };

  const onChangeSelect = (e: any, fieldName: string) => {
    const newActiveCodeTemp = { ...newActiveCode } as any;
    newActiveCodeTemp[fieldName] = {
      id: e,
    };

    setNewActiveCode(newActiveCodeTemp);
  };

  const loadNewActiveCode = async () => {
    try {
      const newActiveCode: ActiveCodeRequest = {
        id: '',
        code: '',
        activeDate: new Date(),
        serialNumber: '',
        isbn: '',
        status: 'INACTIVE',
      };
      setNewActiveCode(newActiveCode);
    } catch (err) {
      processError(err);
    }
  };

  const loadEditActiveCode = async () => {
    try {
      const res = await axiosInstance({
        url: `/active-codes/${activeCodeId}`,
        method: 'get',
      });

      if (res.status === 200) {
        setNewActiveCode(res.data);
      } else {
        message.error('Có lỗi xảy ra.');
      }
    } catch (err) {
      processError(err);
    }
  };

  const onOk = async () => {
    if (!newActiveCode?.code) {
      message.error('Vui lòng nhập mã kích hoạt');
      return;
    }
    if (!newActiveCode?.book || !newActiveCode?.book?.id) {
      message.error('Vui lòng chọn sách');
      return;
    }
    if (!newActiveCode?.status) {
      message.error('Vui lòng chọn trạng thái');
      return;
    }
    try {
      if (newActiveCode) {
        await axiosInstance({
          url: mode === ModePage.ADD ? '/active-codes' : `/active-codes/${newActiveCode.id}`,
          method: mode === ModePage.ADD ? 'post' : 'put',
          data: newActiveCode,
        });
        onBeforeSuccess();
        message.success(mode === ModePage.ADD ? `Thêm thành công` : `Sửa thành công`);
      }
    } catch (err) {
      processError(err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      mode === ModePage.ADD ? loadNewActiveCode() : loadEditActiveCode();
    }
  }, [isOpen]);

  const handleActiveDateChange = (date: dayjs.Dayjs) => {
    newActiveCode &&
      setNewActiveCode({
        ...newActiveCode,
        activeDate: date ? dayjs(date).tz('Asia/Ho_Chi_Minh').toDate() : null,
      });
  };

  return (
    <Modal open={isOpen} onOk={onOk} onCancel={onCloseModal}>
      <div className="mt-8">
        <div className="flex items-center gap-2">
          <div className="w-[210px]">
            Sách: <span className={'text-FF1D30'}>*</span>
          </div>
          <Select
            onChange={(e) => onChangeSelect(e, 'book')}
            className="w-[324px]"
            style={{ flex: 1 }}
            value={newActiveCode && newActiveCode.book ? newActiveCode.book.id : ''}
            options={books.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">
            Mã kích hoạt: <span className={'text-FF1D30'}>*</span>
          </div>
          <Input maxLength={255} name="code" onChange={(e) => onInputField(e, 'code')} value={newActiveCode?.code} />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">
            Trạng thái: <span className={'text-FF1D30'}>*</span>
          </div>
          <Select
            onChange={(e) => onChangeSelect(e, 'grade')}
            className="w-[324px]"
            style={{ flex: 1 }}
            value={newActiveCode?.status}
            options={activeCodeStatusList}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">Ngày kích hoạt</div>
          <DatePicker
            format="YYYY-MM-DD HH:mm:ss"
            value={newActiveCode?.activeDate ? dayjs(newActiveCode?.activeDate) : null}
            showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm:ss') }}
            onChange={handleActiveDateChange}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">Id người kích hoạt</div>
          <Input
            maxLength={255}
            name="userId"
            onChange={(e) => onInputField(e, 'userId')}
            value={newActiveCode?.userId || ''}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">Serial Number</div>
          <Input
            maxLength={255}
            name="serialNumber"
            onChange={(e) => onInputField(e, 'serialNumber')}
            value={newActiveCode?.serialNumber || ''}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-[210px]">ISBN</div>
          <Input
            maxLength={255}
            name="isbn"
            onChange={(e) => onInputField(e, 'isbn')}
            value={newActiveCode?.isbn || ''}
          />
        </div>
      </div>
    </Modal>
  );
}
